import React from 'react';
import SolutionPageTemplate from './SolutionPageTemplate';

const ANALYSE_CODE = `# Analyse a Jira ticket against your codebase
zibby analyze --ticket PROJ-1234

# Output
✓ Located: src/auth/session.ts (referenced by ticket title)
✓ Walked 12 related files via imports
✓ Identified 3 implementation candidates
✓ Generated implementation plan + risk assessment
✓ Suggested 8 test cases for acceptance criteria

📂 Session: .zibby/output/sessions/1768974629717
→ Open in Studio: zibby studio open <session>`;

const CUSTOMIZE_CODE = `// Fork the recipe — same primitives, your business logic.
import { WorkflowGraph, z } from '@zibby/agent-workflow';

const Plan = z.object({
  files: z.array(z.string()),
  changes: z.array(z.object({ file: z.string(), action: z.enum(['add','edit','delete']) })),
  risks: z.array(z.string()),
  testCases: z.array(z.string()),
});

const graph = new WorkflowGraph();
graph
  .addNode('locate',     { agent: 'claude', outputSchema: z.object({ files: z.array(z.string()) }) })
  .addNode('walk',       { agent: 'claude', outputSchema: z.object({ context: z.string() }) })
  .addNode('plan',       { agent: 'claude', outputSchema: Plan })
  .addNode('summarise',  { agent: 'gemini', outputSchema: z.object({ summary: z.string() }) });`;

const CodeAnalysisPage = () => {
  return (
    <SolutionPageTemplate
      meta={{
        title: 'Code analysis — Zibby',
        description: 'Read a Jira ticket, walk the codebase, produce an implementation plan with risks + suggested test cases. Multi-agent recipe with Zod-validated handoff.',
      }}
      eyebrow="RECIPE"
      title="Triage a ticket. Get an implementation plan."
      lede="Point Zibby at a Jira ticket. It walks your codebase, identifies the affected files, lays out the implementation plan, calls out risks, and suggests test cases — before anyone writes a line of code."
      ctaPrimary={{ label: 'Try the recipe →', href: 'https://docs.zibby.app/recipes', external: true }}
      ctaSecondary={{ label: 'See the platform', href: '/solutions/workflow' }}
      sections={[
        {
          title: 'Ticket → context → plan, in one command.',
          body: (
            <>
              <p>
                <code style={{ background: 'rgba(255,255,255,0.06)', padding: '2px 6px', borderRadius: 4, fontFamily: 'SF Mono, Menlo, monospace', fontSize: '13px' }}>zibby analyze</code> reads the ticket, walks imports out from referenced files, builds a structured implementation plan with risks, and proposes acceptance test cases. All Zod-validated, all replayable.
              </p>
              <p>
                Better than asking Claude in your IDE because: <strong>the output is a structured artifact.</strong> A reviewer can scan the plan in 30 seconds. A junior eng can pick it up. The plan is the contract — implementation can happen later, by anyone.
              </p>
            </>
          ),
          code: ANALYSE_CODE,
        },
        {
          title: 'Where it earns its keep.',
          body: (
            <>
              <ul>
                <li><strong>Sprint planning</strong> — analyse the backlog overnight; the morning standup starts with rough estimates already in hand</li>
                <li><strong>On-call triage</strong> — paged at 2am, point Zibby at the alert ticket, get a plan before you finish your coffee</li>
                <li><strong>Onboarding</strong> — new hires read the analysis output instead of walking the codebase blind</li>
                <li><strong>Code review prep</strong> — generate the "what should this PR look like" plan before reviewing the actual diff</li>
              </ul>
            </>
          ),
        },
        {
          title: 'Or fork the recipe — make it yours.',
          body: (
            <>
              <p>
                Need to add a "compliance check" node? Want to swap Claude for Gemini on the planning step? It's just a Zibby agent under the hood. <code>zibby workflow new my-analysis</code> and edit the graph.
              </p>
              <p>
                <strong>The recipe is the proof of concept.</strong> The platform is what you build on.
              </p>
            </>
          ),
          code: CUSTOMIZE_CODE,
        },
      ]}
      cta={{
        title: 'Stop estimating tickets blind.',
        body: 'Free to try. No card required.',
        primary: { label: 'Get started →', href: '/connect' },
        secondary: { label: 'Read the docs', href: 'https://docs.zibby.app', external: true },
      }}
    />
  );
};

export default CodeAnalysisPage;
